import "isomorphic-fetch";
import {ThunkAction} from "redux-thunk";
import {cloneDeep, range, forEach, clone, get} from "lodash";

import {apiError, CommonAction, apiRequest, apiPost} from "./common";
import {Hotel, HotelListingAvailability, Search} from "../models/hotels";
import {ApplicationState} from "../reducers";
import {RouteComponentProps} from "react-router";
import {
    convertOrderResponse,
    DiscountCode,
    Order,
    OrderAndDetail,
    OrderAndListings,
    OrderDiscount,
    OrderResponse,
    OrderTicket, OrderTicketPriceInfo,
    OrderValidationResults,
    prepOrderConfirmation,
} from "../models/order";
import {HotelFilters} from "../reducers/hotels";
import {LOCAL_DATE_FMT, localStorageSet} from "../utils";

export interface HotelAction extends CommonAction {
    searchResults?: HotelListingAvailability[];
    hotel?: Hotel;
    loading?: boolean;
    order?: Order;
    searchCriteria?: Search;
    editAgesOpen?: boolean;
    filters?: HotelFilters;
    validationError?: string;
    newDiscountCode?: string;
    discountCodeLoading?: boolean;
    discountCodeMessage?: string;
    newDiscountSuccess?: boolean;
    orderTicket?: OrderTicket;
    orderTicketLoading?: boolean;
    pauseOrdering?: boolean;
    orderTicketPriceInfo?: OrderTicketPriceInfo;
}

export const HOTEL_LOADING = "HOTEL_LOADING";
export const HOTEL_SEARCH_LOADING = "HOTEL_SEARCH_LOADING";
export const HOTEL_SEARCH_RESULTS = "HOTEL_SEARCH_RESULTS";
export const HOTEL_DETAIL = "HOTEL_DETAIL";
export const HOTEL_UPDATE_SEARCH = "HOTEL_UPDATE_SEARCH";
export const HOTEL_EDIT_AGES = "HOTEL_EDIT_AGES";
export const HOTEL_ORDER_LOADING = "HOTEL_ORDER_LOADING";
export const HOTEL_UPDATE_ORDER = "HOTEL_UPDATE_ORDER";
export const HOTEL_UPDATE_FILTERS = "HOTEL_UPDATE_FILTERS";
export const HOTEL_UPDATE_VALIDATION_ERROR = "HOTEL_UPDATE_VALIDATION_ERROR";
export const HOTEL_API_ERROR = "HOTEL_API_ERROR";
export const HOTEL_UPDATE_DISCOUNT_CODE = "HOTEL_UPDATE_DISCOUNT_CODE";
export const HOTEL_DISCOUNT_LOADING = "HOTEL_DISCOUNT_LOADING";
export const HOTEL_CHECK_DISCOUNT_CODE = "HOTEL_CHECK_DISCOUNT_CODE";
export const HOTEL_RESET_ORDER = "HOTEL_RESET_ORDER";
export const HOTEL_GET_TICKET_PRICE = "HOTEL_GET_TICKET_PRICE";
export const HOTEL_GET_TICKET_PRICE_LOADING = "HOTEL_GET_TICKET_PRICE_LOADING";
export const HOTEL_PAUSE_ORDERING = "HOTEL_PAUSE_ORDERING";
export const HOTEL_SET_TICKET_PRICE_INFO = "HOTEL_SET_TICKET_PRICE_INFO";

export type HotelActionResponse = ThunkAction<Promise<HotelAction>, ApplicationState, {}>;

function prepareSearchPayload(search: Search): any {
    const searchPayload: any = cloneDeep(search);
    searchPayload.checkIn = search.checkIn.format(LOCAL_DATE_FMT);
    searchPayload.checkOut = search.checkOut.format(LOCAL_DATE_FMT);
    return searchPayload;
}

function prepareOrderPayload(order: Order): any {
    const orderPayload: any = cloneDeep(order);
    orderPayload.search = prepareSearchPayload(order.search);
    forEach(orderPayload.hotel, (ho: any) => {
        ho.checkIn = ho.checkIn.format(LOCAL_DATE_FMT);
        ho.checkOut = ho.checkOut.format(LOCAL_DATE_FMT);
    });
    return orderPayload;
}

export function applySearchCriteriaUpdate(search: Search, router: RouteComponentProps<any>): HotelActionResponse {
    return (dispatch) => {
        // If we're on the hotel detail page, requery the detail
        if (router.match.path.indexOf("/hotels/") > -1 && router.match.params.id) {
            return dispatch(hotelDetail(router.match.params.id));
        }
        // Hotel search page
        if (router.match.path.indexOf("/hotels") > -1 && !router.match.params.id) {
            return dispatch(searchHotels(search));
        }
    };
}

export function searchHotels(search: Search): HotelActionResponse {
    return (dispatch) => {
        dispatch(loading(true));
        return apiPost<OrderAndListings>("/packages/search", prepareSearchPayload(search))
            .then(response => {
                const {listings, order} = response;
                return dispatch({
                    type: HOTEL_SEARCH_RESULTS,
                    searchResults: listings,
                    order: convertOrderResponse(order)}) as HotelAction;
            })
            .catch(err => dispatch(apiError(HOTEL_API_ERROR, err)));
    };
}

export function updateSearch(search: Search): HotelAction {
    localStorageSet(HOTEL_UPDATE_SEARCH, JSON.stringify(search));
    return {searchCriteria: search, type: HOTEL_UPDATE_SEARCH};
}

export function updateNumChildren(search: Search, n: number): Search {
    const crit = cloneDeep(search);
    const diff = n - crit.childAges.length;
    if (diff < 0) {
        crit.childAges.splice(n, -diff);
    } else if (diff > 0) {
        range(diff).forEach(() => crit.childAges.push(-1));
    }
    return crit;
}

export function hotelDetail(id: string): HotelActionResponse {
    return (dispatch, getState) => {
        dispatch(loading(true));
        const searchPayload: any = prepareSearchPayload(getState().hotels.searchCriteria);
        return apiPost<OrderAndDetail>(`/packages/hotel/${id}`, searchPayload)
            .then(response => {
                const {order, hotel} = response;
                return dispatch({
                    type: HOTEL_DETAIL,
                    hotel,
                    order: convertOrderResponse(order)
                }) as HotelAction;
            })
            .catch(err => dispatch(apiError(HOTEL_API_ERROR, err)));
    };
}

export function loading(isLoading: boolean): HotelAction {
    return {loading: isLoading, type: HOTEL_LOADING};
}

export function editAges(isOpen: boolean): HotelAction {
    return {editAgesOpen: isOpen, type: HOTEL_EDIT_AGES};
}

export function searchLoading(isLoading: boolean): HotelAction {
    return {loading: isLoading, type: HOTEL_SEARCH_LOADING};
}

export function validationError(msg: string): HotelAction {
    return {validationError: msg, type: HOTEL_UPDATE_VALIDATION_ERROR};
}

export function loadLatestOrder(): HotelActionResponse {
    return (dispatch) => {
        let formParam = "";
        if (window && window.location && window.location.search) {
            const match = (/form=([a-zA-Z0-9]+)/).exec(window.location.search);
            if (match && match.length > 1) {
                formParam = `?form=${match[1]}`;
            }
        }
        dispatch(searchLoading(true));
        return apiRequest<OrderResponse>("/orders/cart" + formParam)
            .then(orderResponse => {
                const order = convertOrderResponse(orderResponse);
                dispatch(updateSearch(order.search));
                return dispatch({
                    type: HOTEL_UPDATE_ORDER,
                    order
                }) as HotelAction;
            })
            .catch(err => dispatch(apiError(HOTEL_API_ERROR, err)));
    };
}

export function orderLoading(isLoading: boolean): HotelAction {
    return {loading: isLoading, type: HOTEL_ORDER_LOADING};
}

export function updateOrder(order: Order): HotelActionResponse {
    return (dispatch) => {
        //dispatch(orderLoading(true));
        return apiPost<OrderValidationResults>(`/orders/cart`, prepareOrderPayload(order))
            .then(response => {
                if (response.isValid) {
                    return dispatch({
                        type: HOTEL_UPDATE_ORDER,
                        order: convertOrderResponse(response.order)
                    });
                } else {
                    return dispatch({
                        type: HOTEL_RESET_ORDER,
                        validationError: response.validationErrorMessage
                    });
                }
            })
            .catch(err => dispatch(apiError(HOTEL_API_ERROR, err)));
    };
}

export function validateCompletedOrder(order: Order): HotelActionResponse {
    return (dispatch) => {
        dispatch(orderLoading(true));
        return apiPost<OrderValidationResults>(`/orders/cart/validate`, prepareOrderPayload(order))
            .then(response => {
                if (response.isValid) {
                    return dispatch({
                        type: HOTEL_UPDATE_ORDER,
                        order: convertOrderResponse(response.order)
                    });
                } else {
                    return dispatch({
                        type: HOTEL_UPDATE_VALIDATION_ERROR,
                        validationError: response.validationErrorMessage
                    });
                }
            })
            .catch(err => dispatch(apiError(HOTEL_API_ERROR, err))) as Promise<HotelAction>;
    };
}

export function placeOrder(order: Order): HotelActionResponse {
    return (dispatch) => {
        dispatch(orderLoading(true));
        return apiPost<OrderValidationResults>(`/orders`, prepareOrderPayload(order))
            .then(response => {
                if (response.isValid) {
                    return dispatch({
                        type: HOTEL_UPDATE_ORDER,
                        order: prepOrderConfirmation(response.order)
                    });
                } else {
                    return dispatch({
                        type: HOTEL_UPDATE_VALIDATION_ERROR,
                        validationError: response.validationErrorMessage
                    });
                }
            })
            .catch(err => dispatch(apiError(HOTEL_API_ERROR, err))) as Promise<HotelAction>;
    };
}

export function updateFilters(filters: HotelFilters): HotelAction {
    return {filters, type: HOTEL_UPDATE_FILTERS};
}

export function updateOrderStore(order: Order): HotelAction {
    return ({
        type: HOTEL_UPDATE_ORDER,
        order: clone(order)
    });
}

export function updateDiscountCode(code: string): HotelAction {
    return ({
        type: HOTEL_UPDATE_DISCOUNT_CODE,
        newDiscountCode: code
    });
}
export const discountLoading = (loading: boolean) => ({type: HOTEL_DISCOUNT_LOADING, discountCodeLoading: loading});
export const discountMessage = (msg: string, success: boolean) =>
    ({
        type: HOTEL_CHECK_DISCOUNT_CODE,
        discountCodeMessage: msg,
        newDiscountSuccess: success
    });

export function removeDiscountCode(orderDiscount: OrderDiscount): HotelActionResponse {
    return (dispatch, getState) => {
        const order = getState().hotels.order;
        const ix = order.discounts.indexOf(orderDiscount);
        if (ix > -1) {
            order.discounts.splice(ix, 1);
        }
        return dispatch(updateOrder(order)).then(() =>
            dispatch(discountMessage(orderDiscount.discountName + " removed", false)));
    };
}

export function checkDiscountCode(code: string): HotelActionResponse {
    return (dispatch, getState) => {
        dispatch(discountLoading(true));
        return apiRequest<DiscountCode>(`/discounts/lookup/${encodeURIComponent(code)}`)
            .then(response => {
                // Got a good code, apply it to the order
                const order = getState().hotels.order;
                order.discounts.push({
                    discountId: response.id,
                    discountName: response.name
                });
                return dispatch(updateOrder(order))
                    .then(() => {
                        return dispatch(discountMessage(`Successfully applied discount code ${code}`, true));
                    })
            })
            .catch(err => {
                return dispatch(discountMessage(`Invalid discount code ${code}`, false))
            }) as Promise<HotelAction>;
    };
}

export const getTicketPrice = (orderTicket: OrderTicket) => ({type: HOTEL_GET_TICKET_PRICE, orderTicket});
export const loadingTicketPrice = (loading: boolean) => ({type: HOTEL_GET_TICKET_PRICE_LOADING, orderTicketLoading: loading});
export const setPauseOrdering = (paused: boolean) => ({type: HOTEL_PAUSE_ORDERING, pauseOrdering: paused});
export const setTicketPriceInfo = (info: OrderTicketPriceInfo) => ({type: HOTEL_SET_TICKET_PRICE_INFO, orderTicketPriceInfo: info});

export function loadOrderTicketPrice(orderTicket: OrderTicket): HotelActionResponse {
    return (dispatch, state) => {
        dispatch(loadingTicketPrice(true));
        const form = get(state(), "hotels.order.form");
        const formParam = form ? `?form=${form}` : "";
        return apiPost<OrderTicketPriceInfo>(`/tickets/price${formParam}`, orderTicket)
            .then(response => {
                return dispatch(setTicketPriceInfo(response));
            })
            .catch( err => {
                dispatch(loadingTicketPrice(false));
                return dispatch(apiError(HOTEL_API_ERROR, err))
            });
    };
}



// WEBPACK FOOTER //
// ./src/actions/hotels.ts